import { useCallback, useEffect } from 'react';

import {
  INCOMING_MESSAGE,
  messagePlatformReady,
  messagePlatformInfo,
  mapWorkspaceForInfinity,
  mapUserForInfinity,
  mapCustomerForInfinity,
} from 'src/data/infinity';
import { sendMessageToParent, subscribeToMessages } from 'src/lib/messages';
import * as events from 'src/lib/events';
import { useHistory } from 'src/lib/hooks';
import { ROUTES } from 'src/routes';
import { useUserActions, useCustomerActions, useWorkspaceActions } from 'src/store';
import { useGetCommonEventAuthAttributes, useGetSystemEventAttributes } from 'src/services';

export const useSetupExtensionCommunication = () => {
  const history = useHistory();
  const userActions = useUserActions();
  const customerActions = useCustomerActions();
  const workspaceActions = useWorkspaceActions();
  const getCommonEventAuthAttributes = useGetCommonEventAuthAttributes();
  const getSystemEventAttributes = useGetSystemEventAttributes();
  const trackExtensionEvent = useTrackExtensionEvent();

  // Set up base communication with extension
  useEffect(() => {
    /**
     * Handle incoming "check info" message from Extension
     */
    const processPlatformInfoCheckMessage = () => {
      const user = userActions.getCurrentUser();
      const customer = customerActions.getCurrentCustomer();
      const workspace = workspaceActions.getCurrentWorkspace();

      const message = messagePlatformInfo({
        authenticated: Boolean(user),
        user: user ? mapUserForInfinity(user) : null,
        customer: customer ? mapCustomerForInfinity(customer) : null,
        workspace: workspace ? mapWorkspaceForInfinity(workspace) : null,
      });
      sendMessageToParent(message);
    };

    /**
     * Log in the Platform
     */
    const processLoginMessage = async (content) => {
      try {
        await userActions.loginWithToken(content?.token);
        trackExtensionEvent(events.INFINITY.loggedIn, content.attributes);

        history.replace(ROUTES.ROOT);
      } catch (error) {
        history.replace(ROUTES.SIGN_IN);
      }
    };

    /**
     * Logout from the Platform
     */
    const processLogoutMessage = () => {
      trackExtensionEvent(events.SYSTEM.logout, {
        reason: events.LOGOUT_REASON.extensionMessage,
      });

      userActions.logout(true);
      history.replace(ROUTES.SIGN_IN);
    };

    /**
     * Track event from Extension
     * @param {object} content - event data
     */
    const processTrackEventMessage = (content) => {
      const name = events.prefixExtensionEventName(content.name);
      trackExtensionEvent(name, content.attributes);
    };

    /**
     * Activate customer/workspace
     */
    const processActivateWorkspaceMessage = async (content) => {
      try {
        if (content.customerId) {
          await customerActions.selectCurrentCustomer(content.customerId);
        }
        if (content.workspaceId) {
          await workspaceActions.selectCurrentWorkspace(content.workspaceId);
        }
      } catch (error) {
        // do nothing for now
      }
    };

    // Subscribe to parent messages
    const unsubscribe = subscribeToMessages((message) => {
      if (!message?.data) {
        return;
      }

      const { type, content } = message.data;

      // eslint-disable-next-line default-case
      switch (type) {
        case INCOMING_MESSAGE.platformInfoCheck: {
          processPlatformInfoCheckMessage();
          break;
        }

        case INCOMING_MESSAGE.login: {
          processLoginMessage(content);
          break;
        }

        case INCOMING_MESSAGE.logout: {
          processLogoutMessage();
          break;
        }

        case INCOMING_MESSAGE.trackEvent: {
          processTrackEventMessage(content);
          break;
        }

        case INCOMING_MESSAGE.activateWorkspace: {
          processActivateWorkspaceMessage(content);
          break;
        }
      }
    });

    // Notify Extension that Platform is ready for messaging
    const message = messagePlatformReady();
    sendMessageToParent(message);

    return () => {
      unsubscribe();
    };
  }, [
    userActions,
    customerActions,
    workspaceActions,
    getCommonEventAuthAttributes,
    getSystemEventAttributes,
    history,
    trackExtensionEvent,
  ]);
};

const useTrackExtensionEvent = () => {
  const getCommonEventAuthAttributes = useGetCommonEventAuthAttributes();
  const getSystemEventAttributes = useGetSystemEventAttributes();

  return useCallback(
    (name, attributes) => {
      const mergedAttributes = {
        ...getCommonEventAuthAttributes(),
        ...getSystemEventAttributes(),
        ...attributes,
        page: events.EVENT_PAGE.infinity,
      };
      events.trackUserEventNoAuth(name, mergedAttributes);
    },
    [getCommonEventAuthAttributes, getSystemEventAttributes]
  );
};
